import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, updateDoc, doc, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useToast } from '../contexts/ToastContext';
import { GEMINI_API_KEY } from '../config/firebase';

export default function Messaging({ user, initialChatId, navigateTo, initialMessage, db, storage }) {
    const [chats, setChats] = useState([]);
    const [activeChat, setActiveChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState(initialMessage || '');
    const messagesEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const lastReadMarkerShown = useRef(false);
    const textareaRef = useRef(null);
    
    const [isChatLoading, setIsChatLoading] = useState(false);
    const previousChatIdRef = useRef(null);
    const lastInitializedChatId = useRef(null); 
    
    const toast = useToast();
    const [isPolishing, setIsPolishing] = useState(false);

    const [attachments, setAttachments] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0); 
    const fileInputRef = useRef(null);
    const [previewImage, setPreviewImage] = useState(null);

    useEffect(() => {
        if (initialMessage) {
            setNewMessage(initialMessage);
        }
    }, [initialMessage]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = '0px'; 
            const scrollHeight = textareaRef.current.scrollHeight;
            const maxHeight = 300;
            textareaRef.current.style.height = scrollHeight > maxHeight ? `${maxHeight}px` : `${Math.max(scrollHeight, 48)}px`;
            textareaRef.current.style.overflowY = scrollHeight > maxHeight ? 'auto' : 'hidden';
        }
    }, [newMessage]);

    useEffect(() => {
        if (initialChatId && chats.length > 0 && !activeChat && lastInitializedChatId.current !== initialChatId) {
            const chat = chats.find(c => c.id === initialChatId);
            if (chat) {
                setIsChatLoading(true);
                setActiveChat(chat);
                lastInitializedChatId.current = initialChatId;
            }
        } else if (!initialChatId && activeChat) {
            setActiveChat(null);
            lastInitializedChatId.current = null;
        }
    }, [initialChatId, chats, activeChat]);

    useEffect(() => {
        if (!user || user.isAnonymous) return;
        const q = query(collection(db, "chats"), where('participants', 'array-contains', user.uid));
        const unsubscribe = onSnapshot(q, snapshot => {
            const sortedChats = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .sort((a, b) => b.updatedAt?.seconds - a.updatedAt?.seconds);
            setChats(sortedChats);
        });
        return unsubscribe;
    }, [user, db]);

    useEffect(() => {
        if (!activeChat) {
            setMessages([]);
            return;
        }

        setIsChatLoading(true);
        setMessages([]); 

        const chatRef = doc(db, "chats", activeChat.id);
        updateDoc(chatRef, { [`lastRead.${user.uid}`]: serverTimestamp() })
          .catch(err => console.error("Failed to update last read time:", err));
          
        const messagesCol = collection(db, `chats/${activeChat.id}/messages`);
        const q = query(messagesCol, orderBy('createdAt', 'asc'));
        const unsubscribe = onSnapshot(q, snapshot => {
            const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setMessages(msgs);
            setIsChatLoading(false); 
        });
        return unsubscribe;
    }, [activeChat, user.uid, db]);

    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container || isChatLoading) return;
        
        const isNewChat = previousChatIdRef.current !== activeChat?.id;
        const isScrolledToBottom = container.scrollHeight - container.clientHeight <= container.scrollTop + 100;

        if (isScrolledToBottom || isNewChat) {
            if (isNewChat) {
                container.scrollTop = container.scrollHeight;
            } else {
                container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
            }
            
            if (isNewChat) {
                textareaRef.current?.focus({ preventScroll: true });
                previousChatIdRef.current = activeChat?.id;
            }
        }
        
        lastReadMarkerShown.current = false;
    }, [messages, isChatLoading, activeChat?.id]);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (files.length > 0) {
            const newAttachments = files.map(file => ({
                file,
                preview: file.type.includes('image') ? URL.createObjectURL(file) : null,
                type: file.type.includes('pdf') ? 'pdf' : 'image',
                name: file.name
            }));
            setAttachments(prev => [...prev, ...newAttachments]);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSendMessage = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if ((newMessage.trim() === '' && attachments.length === 0) || !activeChat) return;

        const prohibitedTerms = ['venmo', 'zelle', 'paypal', 'cashapp', 'pay directly', 'wire transfer', 'pay in cash', 'pay cash'];
        const lowerMessage = newMessage.toLowerCase();
        if (prohibitedTerms.some(term => lowerMessage.includes(term))) {
            toast.error("For your protection, please keep payments on Host-it!. Off-platform payment methods are prohibited.");
            return;
        }

        setIsUploading(true);
        setUploadProgress(0); 
        let uploadedAttachments = [];

        if (attachments.length > 0) {
            try {
                const progressMap = {};
                uploadedAttachments = await Promise.all(attachments.map((att, index) => {
                    return new Promise((resolve, reject) => {
                        const storagePath = `chats/${activeChat.id}/${Date.now()}_${att.file.name}`;
                        const fileRef = ref(storage, storagePath);
                        const uploadTask = uploadBytesResumable(fileRef, att.file);

                        uploadTask.on('state_changed',
                            (snapshot) => {
                                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                progressMap[index] = progress;
                                const total = Object.values(progressMap).reduce((a, b) => a + b, 0) / attachments.length;
                                setUploadProgress(total);
                            },
                            (err) => reject(err),
                            async () => {
                                progressMap[index] = 100;
                                const total = Object.values(progressMap).reduce((a, b) => a + b, 0) / attachments.length;
                                setUploadProgress(total);
                                const url = await getDownloadURL(uploadTask.snapshot.ref);
                                resolve({ url, type: att.type, name: att.name });
                            }
                        );
                    });
                }));
            } catch (err) {
                console.error("Upload failed", err);
                toast.error("Failed to upload files.");
                setIsUploading(false);
                setUploadProgress(0);
                return;
            }
        }

        const messageData = { 
            text: newMessage.trim(), 
            senderId: user.uid, 
            createdAt: serverTimestamp() 
        };
        
        if (uploadedAttachments.length > 0) {
            messageData.attachments = uploadedAttachments;
        }

        await addDoc(collection(db, `chats/${activeChat.id}/messages`), messageData);
        
        let summaryText = newMessage.trim();
        if (!summaryText) {
            if (uploadedAttachments.length > 1) {
                summaryText = `Sent ${uploadedAttachments.length} attachments 📎`;
            } else if (uploadedAttachments.length === 1) {
                summaryText = uploadedAttachments[0].type === 'pdf' ? 'Sent a document 📄' : 'Sent an image 📷';
            }
        }
        await updateDoc(doc(db, "chats", activeChat.id), { 
            lastMessage: summaryText, 
            lastMessageSenderId: user.uid, 
            updatedAt: serverTimestamp() 
        });
        
        setNewMessage('');
        setAttachments([]);
        setIsUploading(false);
    };

    const handleAiPolish = async () => {
        if (!newMessage.trim()) return;
        setIsPolishing(true);
        try {
            const prompt = `Rewrite the following chat message to be more professional, clear, and polite. It is sent from a ${user.isVendor ? 'vendor' : 'customer'} on a party rental platform.\n\nOriginal Message: "${newMessage}"\n\nRewritten Message (just the text):`;
            const payload = { contents: [{ role: "user", parts: [{ text: prompt }] }] };
            // Note: replace GEMINI_API_KEY with your actual config import
            const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-09-2025:generateContent?key=YOUR_API_KEY_HERE`;
            
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.candidates?.[0]) {
                setNewMessage(result.candidates[0].content.parts[0].text.trim());
            }
        } catch (err) {
            console.error("AI Polish Error:", err);
        } finally {
            setIsPolishing(false);
        }
    };

    const handleSelectChat = (chat) => {
        setIsChatLoading(true);
        setActiveChat(chat);
        setNewMessage(''); 
        lastInitializedChatId.current = chat.id; 
        navigateTo('messages', { chatId: chat.id });
    };

    return (
        <div className="container mx-auto h-[85vh]">
            <div className="h-full flex bg-white rounded-lg shadow-xl border">
                {/* Chat List Sidebar */}
                <div className={`w-full md:w-1/3 border-r border-gray-200 flex-col ${activeChat ? 'hidden' : 'flex'} md:flex`}>
                    <div className="p-4 border-b border-gray-200"><h2 className="text-xl font-bold">Conversations</h2></div>
                    <ul className="overflow-y-auto flex-grow">
                        {chats.map(chat => {
                            const otherUserId = chat.participants?.find(id => id !== user.uid);
                            const otherUserInfo = chat.participantInfo ? chat.participantInfo[otherUserId] : null;
                            const displayName = otherUserInfo?.name || chat.participantEmails?.find(email => email !== user.email) || 'Chat';
                            const profilePicUrl = otherUserInfo?.profilePicUrl || '[https://placehold.co/48x48/e2e8f0/64748b?text=U](https://placehold.co/48x48/e2e8f0/64748b?text=U)';
                            const lastMessage = chat.lastMessage || '';
                            
                            const currentUserLastRead = chat.lastRead?.[user.uid];
                            const isUnread = chat.lastMessageSenderId && chat.lastMessageSenderId !== user.uid && (!currentUserLastRead || (chat.updatedAt && chat.updatedAt.seconds > currentUserLastRead.seconds));

                            return (
                                <li key={chat.id} onClick={() => handleSelectChat(chat)} className={`cursor-pointer hover:bg-gray-100 ${activeChat?.id === chat.id ? 'bg-pink-50' : ''}`}>
                                    <div className="flex items-center gap-3 p-4 relative">
                                        {isUnread && <div className="absolute left-2 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-pink-500 rounded-full shadow-sm"></div>}
                                        <img src={profilePicUrl} alt={displayName} className="w-12 h-12 rounded-full object-cover flex-shrink-0 bg-gray-200" onError={(e) => { e.target.onerror = null; e.target.src='[https://placehold.co/48x48/e2e8f0/64748b?text=U](https://placehold.co/48x48/e2e8f0/64748b?text=U)'; }}/>
                                        <div className="flex-grow overflow-hidden">
                                            <p className={`truncate ${isUnread ? 'font-bold text-gray-900' : 'font-semibold'}`}>{displayName}</p>
                                            <p className={`text-sm truncate ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>{lastMessage}</p>
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>

                {/* Chat Window */}
                <div className={`w-full md:w-2/3 flex-col ${activeChat ? 'flex' : 'hidden md:flex'}`}>
                    {activeChat ? (
                        <>
                            <div className="p-4 border-b border-gray-200 flex items-center">
                                <button onClick={() => { setActiveChat(null); navigateTo('messages'); }} className="md:hidden mr-4 text-gray-600 hover:text-gray-900">
                                    <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                                </button>
                                <h3 className="font-bold text-lg">{
                                    (() => {
                                        const otherUserId = activeChat.participants?.find(id => id !== user.uid);
                                        const otherUserInfo = activeChat.participantInfo ? activeChat.participantInfo[otherUserId] : null;
                                        return otherUserInfo?.name || activeChat.participantEmails?.find(email => email !== user.email) || 'Chat';
                                    })()
                                }</h3>
                            </div>
                            
                            <div ref={chatContainerRef} className="flex-grow p-4 overflow-y-auto">
                                {messages.map(msg => {
                                    const senderInfo = activeChat.participantInfo?.[msg.senderId];
                                    const profilePicUrl = senderInfo?.profilePicUrl || '[https://placehold.co/32x32/e2e8f0/64748b?text=U](https://placehold.co/32x32/e2e8f0/64748b?text=U)';
                                    const isCurrentUser = msg.senderId === user.uid;

                                    const currentUserLastRead = activeChat.lastRead?.[user.uid];
                                    let showUnreadMarker = false;
                                    if (currentUserLastRead && msg.createdAt && msg.createdAt.seconds > currentUserLastRead.seconds && !lastReadMarkerShown.current) {
                                        if (!isCurrentUser) {
                                            showUnreadMarker = true;
                                            lastReadMarkerShown.current = true;
                                        }
                                    }

                                    const msgImages = msg.attachments?.filter(a => a.type === 'image') || [];
                                    const msgPdfs = msg.attachments?.filter(a => a.type === 'pdf') || [];

                                    return (
                                        <React.Fragment key={msg.id}>
                                            {showUnreadMarker && (
                                                <div className="relative my-4">
                                                    <div className="absolute inset-0 flex items-center" aria-hidden="true"><div className="w-full border-t border-red-300"></div></div>
                                                    <div className="relative flex justify-center"><span className="bg-white px-2 text-sm text-red-500 font-semibold">New Messages</span></div>
                                                </div>
                                            )}
                                            <div className={`flex items-end gap-2 mb-4 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                                                {!isCurrentUser && (
                                                    <img src={profilePicUrl} alt="Sender" className="w-8 h-8 rounded-full object-cover flex-shrink-0 shadow-sm" onError={(e) => { e.target.onerror = null; e.target.src='[https://placehold.co/32x32/e2e8f0/64748b?text=U](https://placehold.co/32x32/e2e8f0/64748b?text=U)'; }}/>
                                                )}
                                                
                                                <div className={`max-w-[85%] sm:max-w-[75%] lg:max-w-[60%] flex flex-col gap-1.5 ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                                                    {msgImages.length > 0 && (
                                                        <div className={`flex gap-1.5 w-full [&::-webkit-scrollbar]:hidden ${msgImages.length > 1 ? 'overflow-x-auto snap-x snap-mandatory pb-1' : ''}`} style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                                                            {msgImages.map((img, i) => (
                                                                <div 
                                                                    key={i} 
                                                                    className={`relative overflow-hidden flex-shrink-0 snap-center cursor-zoom-in group border border-black/5 shadow-sm ${msgImages.length === 1 ? 'rounded-2xl max-w-[260px] sm:max-w-[320px]' : 'rounded-xl w-[180px] h-[240px] sm:w-[220px] sm:h-[280px]'}`}
                                                                    onClick={() => setPreviewImage(img.url)}
                                                                >
                                                                    <img src={img.url} alt="Attachment" className="w-full h-full object-cover transition-opacity duration-200 group-hover:opacity-90" />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {msgPdfs.length > 0 && (
                                                        <div className="flex flex-col gap-1.5 w-full max-w-[280px]">
                                                            {msgPdfs.map((pdf, i) => (
                                                                <a key={i} href={pdf.url} target="_blank" rel="noopener noreferrer" className={`flex items-center gap-3 p-3 rounded-2xl border ${isCurrentUser ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-800'} transition shadow-sm`}>
                                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0 ${isCurrentUser ? 'bg-blue-500' : 'bg-gray-100'}`}>📄</div>
                                                                    <span className="text-sm font-semibold underline truncate">{pdf.name || 'Document.pdf'}</span>
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {msg.text && (
                                                        <div className={`px-4 py-2.5 text-[15px] leading-relaxed shadow-sm ${isCurrentUser ? 'bg-blue-500 text-white rounded-3xl rounded-br-sm' : 'bg-[#e5e5ea] text-black rounded-3xl rounded-bl-sm'}`}>
                                                            <p className="break-words whitespace-pre-wrap">{msg.text}</p>
                                                        </div>
                                                    )}
                                                </div>

                                                {isCurrentUser && (
                                                    <img src={user.profilePicUrl || profilePicUrl} alt="You" className="w-8 h-8 rounded-full object-cover flex-shrink-0 shadow-sm" onError={(e) => { e.target.onerror = null; e.target.src='[https://placehold.co/32x32/e2e8f0/64748b?text=U](https://placehold.co/32x32/e2e8f0/64748b?text=U)'; }}/>
                                                )}
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            <form onSubmit={handleSendMessage} className="relative p-4 border-t border-gray-200 flex flex-col gap-2 bg-gray-50">
                                {isUploading && uploadProgress > 0 && (
                                    <div className="absolute bottom-full left-0 w-full mb-2 px-4 z-10 animate-fade-in">
                                        <div className="bg-white p-3 rounded-xl shadow-md border border-gray-200 flex flex-col gap-2">
                                            <div className="flex justify-between items-center text-xs font-bold text-gray-600">
                                                <span>Sending {attachments.length} attachment(s)...</span>
                                                <span className="text-pink-600">{Math.round(uploadProgress)}%</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden shadow-inner">
                                                <div className="bg-pink-500 h-full rounded-full transition-all duration-200 ease-out" style={{ width: `${uploadProgress}%` }}></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {attachments.length > 0 && (
                                    <div className="flex gap-2 p-2 overflow-x-auto bg-white border border-gray-200 rounded-xl shadow-sm mb-2 max-w-full custom-scrollbar">
                                        {attachments.map((att, idx) => (
                                            <div key={idx} className="relative flex-shrink-0 group">
                                                {att.type === 'pdf' ? (
                                                    <div className="w-16 h-16 flex flex-col items-center justify-center bg-gray-50 rounded-lg border border-gray-200 px-1">
                                                        <span className="text-2xl">📄</span>
                                                        <span className="text-[8px] truncate w-full text-center text-gray-500 mt-1">{att.name}</span>
                                                    </div>
                                                ) : (
                                                    <img src={att.preview} alt="Attachment Preview" className="h-16 w-16 object-cover rounded-lg border border-gray-200" />
                                                )}
                                                <button type="button" onClick={() => removeAttachment(idx)} disabled={isUploading} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors disabled:opacity-50 opacity-0 group-hover:opacity-100 md:opacity-100">
                                                    <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="flex gap-2 items-end">
                                    <input type="file" multiple ref={fileInputRef} onChange={handleFileSelect} className="hidden" accept="image/*,application/pdf" />
                                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={isUploading || isPolishing} className="p-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors disabled:opacity-50 flex-shrink-0" >
                                        <svg xmlns="[http://www.w3.org/2000/svg](http://www.w3.org/2000/svg)" className="h-6 w-6 transform rotate-45" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                                    </button>
                                    <div className="relative flex-grow bg-white border border-gray-300 rounded-xl focus-within:ring-2 focus-within:ring-pink-500 focus-within:border-transparent transition-all shadow-sm">
                                        <textarea 
                                            ref={textareaRef}
                                            value={newMessage} 
                                            onChange={e => setNewMessage(e.target.value)} 
                                            onKeyDown={e => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage(e);
                                                }
                                            }}
                                            placeholder="Type a message..." 
                                            rows={1}
                                            className="w-full p-3 pr-24 bg-transparent border-none outline-none resize-none disabled:bg-gray-100 rounded-xl block"
                                            disabled={isPolishing || isUploading}
                                            style={{ minHeight: '48px' }}
                                        />
                                        {newMessage.length > 3 && (
                                            <button type="button" onClick={handleAiPolish} disabled={isPolishing || isUploading} className="absolute right-2 bottom-2 text-xs bg-purple-100 text-purple-700 px-3 py-1.5 rounded-full hover:bg-purple-200 font-bold transition flex items-center gap-1.5 z-20 shadow-sm" >
                                                {isPolishing ? <span className="animate-spin">✨</span> : <span>✨ Polish</span>}
                                            </button>
                                        )}
                                    </div>
                                    {user?.isVendor && (
                                        <MessageTemplatesManager user={user} db={db} onSelectTemplate={(text) => setNewMessage(text)} />
                                    )}
                                    <button type="submit" disabled={isPolishing || isUploading || (newMessage.trim() === '' && attachments.length === 0)} className="bg-pink-500 text-white px-6 h-[48px] rounded-xl hover:bg-pink-600 transition font-bold flex-shrink-0 shadow-sm flex items-center justify-center disabled:opacity-50 disabled:bg-gray-400">
                                        {isUploading ? <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span> : 'Send'}
                                    </button>
                                </div>
                            </form>
                        </>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500 p-8 text-center bg-gray-50 rounded-r-lg">
                            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 border border-gray-100">
                                <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <h3 className="text-xl font-bold text-gray-700 mb-2">Your Messages</h3>
                            <p className="max-w-xs mx-auto text-sm text-gray-500 leading-relaxed">
                                Select a conversation from the list to view details or start chatting with a vendor about your event.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {previewImage && (
                <LargePreviewModal imageUrl={previewImage} onClose={() => setPreviewImage(null)} />
            )}
        </div>
    );
}

function MessageTemplatesManager({ user, db, onSelectTemplate }) {
    const [templates, setTemplates] = useState(user?.messageTemplates || []);
    const [isOpen, setIsOpen] = useState(false);
    const [isManaging, setIsManaging] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newContent, setNewContent] = useState('');

    const handleSave = async () => {
        if (!newTitle || !newContent) return;
        const updated = [...templates, { id: Date.now().toString(), title: newTitle, content: newContent }];
        setTemplates(updated);
        await updateDoc(doc(db, "users", user.uid), { messageTemplates: updated });
        setNewTitle('');
        setNewContent('');
    };

    const handleDelete = async (id) => {
        const updated = templates.filter(t => t.id !== id);
        setTemplates(updated);
        await updateDoc(doc(db, "users", user.uid), { messageTemplates: updated });
    };

    return (
        <div className="relative">
            <button 
                type="button" 
                onClick={() => setIsOpen(!isOpen)}
                className="p-3 h-[48px] bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl transition-colors flex-shrink-0 flex items-center justify-center"
                title="Message Templates"
            >
                📋
            </button>

            {isOpen && (
                <div className="absolute bottom-full right-0 mb-2 w-72 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden z-50 animate-fade-in flex flex-col max-h-[400px]">
                    <div className="p-3 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
                        <span className="font-bold text-gray-800 text-sm">Quick Replies</span>
                        <button type="button" onClick={() => setIsManaging(!isManaging)} className="text-xs text-blue-600 hover:underline font-bold">
                            {isManaging ? 'Done' : 'Manage'}
                        </button>
                    </div>
                    
                    <div className="overflow-y-auto flex-grow p-2 space-y-2 custom-scrollbar">
                        {templates.length === 0 && !isManaging && (
                            <p className="text-xs text-gray-500 text-center py-4">No templates saved. Click Manage to add one.</p>
                        )}
                        
                        {templates.map(t => (
                            <div key={t.id} className="bg-white p-3 rounded-lg border border-gray-100 shadow-sm group flex flex-col gap-1">
                                <div className="flex justify-between items-start">
                                    <span className="font-bold text-sm text-gray-800">{t.title}</span>
                                    {isManaging ? (
                                        <button type="button" onClick={() => handleDelete(t.id)} className="text-red-500 hover:text-red-700 text-xs font-bold p-1">✕</button>
                                    ) : (
                                        <button type="button" onClick={() => { onSelectTemplate(t.content); setIsOpen(false); }} className="text-blue-600 hover:text-blue-800 text-[10px] font-bold uppercase tracking-wider bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition">Use</button>
                                    )}
                                </div>
                                <p className="text-xs text-gray-500 line-clamp-2 mt-1">{t.content}</p>
                            </div>
                        ))}
                    </div>

                    {isManaging && (
                        <div className="p-3 bg-gray-50 border-t border-gray-200 flex flex-col gap-2">
                            <input type="text" placeholder="Template Title" value={newTitle} onChange={e => setNewTitle(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 outline-none" />
                            <textarea placeholder="Message Content" value={newContent} onChange={e => setNewContent(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-sm h-20 resize-none focus:ring-2 focus:ring-pink-500 outline-none" />
                            <button type="button" onClick={handleSave} className="w-full bg-gray-900 text-white py-2.5 rounded-lg text-sm font-bold hover:bg-black transition shadow-sm mt-1">Save Template</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function LargePreviewModal({ imageUrl, onClose }) {
    return (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-[400] p-4 cursor-zoom-out" onClick={onClose}>
            <div className="relative w-full h-full max-w-7xl flex items-center justify-center animate-fade-in" onClick={(e) => e.stopPropagation()}>
                <img 
                    src={imageUrl} 
                    alt="Expanded preview" 
                    className="max-w-full max-h-full object-contain rounded-lg shadow-2xl cursor-default" 
                />
                <button
                    onClick={onClose}
                    className="fixed top-4 right-4 bg-white/10 hover:bg-white/20 backdrop-blur-md text-white rounded-full h-10 w-10 flex items-center justify-center text-xl font-bold shadow-lg transition z-50 border border-white/20 cursor-pointer"
                >
                    &times;
                </button>
            </div>
        </div>
    );
}
